/* oxlint-disable acme/no-process-env */
import { createEnv } from "@t3-oss/env-nextjs";
import { z } from "zod";

// Inlined here instead of importing from @acme/utils/dev-url because this file
// is loaded during next.config.ts evaluation, which can't resolve .ts imports
// from workspace packages.
function getBaseUrl() {
  const baseDomain = process.env.BASE_DOMAIN ?? "simcall.us";
  if (process.env.NODE_ENV === "development") {
    const domain = process.env.LOCAL_DEV_DOMAIN ?? `local.${baseDomain}`;
    return `https://${domain}`;
  }
  return process.env.NEXT_PUBLIC_VERCEL_PROJECT_PRODUCTION_URL
    ? `https://${process.env.NEXT_PUBLIC_VERCEL_PROJECT_PRODUCTION_URL}`
    : `https://www.${baseDomain}`;
}

export const env = createEnv({
  /**
   * Specify your server-side environment variables schema here. This way you can ensure the app
   * isn't built with invalid env vars.
   */
  server: {
    DATABASE_URL: z.url(),
    BETTER_AUTH_SECRET: z.string(),
    NETRIFI_API_KEY: z.string(),
    NETRIFI_API_KEY_2: z.string(),
    DRIZZLE_LOG: z.enum(["true", "false"]).prefault("false"),
    SUPER_ORG_CUSTOM_EMAILS: z.string(),
    SUPER_ORG_EMAIL_DOMAIN: z.string(),
    WEBHOOK_SECRET: z.string(),
    INTERNAL_API_KEY: z.string(),
    SENTRY_AUTH_TOKEN: z.string(),
    TRIGGER_SECRET_KEY: z.string(),
    TELEGRAM_BOT_TOKEN: z.string(),
    TELEGRAM_INFO_CHAT_ID: z.string(),
    TELEGRAM_WARNING_CHAT_ID: z.string(),
    TELEGRAM_ERROR_CHAT_ID: z.string(),
    TELEGRAM_USER_FEEDBACK_CHAT_ID: z.string(),
    AWS_SIMCALL_OLD_DB_URL: z.url(),
    WHITELISTED_SERVICES_PROXY_IP: z.string(),
    WHITELISTED_SERVICES_PROXY_PORT: z.string(),
    WHITELISTED_SERVICES_PROXY_USERNAME: z.string(),
    WHITELISTED_SERVICES_PROXY_PASSWORD: z.string(),
    EDGE_CONFIG: z.url(),
    RESEND_API_KEY: z.string(),
    SLACK_BOT_TOKEN: z.string(),
    SLACK_APP_TOKEN: z.string(),
    AWS_REGION: z.string().min(1),
    AWS_ACCESS_KEY_ID: z.string().min(1),
    AWS_SECRET_ACCESS_KEY: z.string().min(1),
    AUTH_IMAGES_CDN_URL: z.url(),
    GC_SERVICE_ACCOUNT_EMAIL: z.string().min(1),
    GC_SERVICE_ACCOUNT_PRIVATE_KEY: z.string().min(1),
    MISTRAL_API_KEY: z.string().min(1),
    DISABLE_REACT_SCAN: z.enum(["true", "false"]).prefault("true"),
    SMS_GATEWAY_CALLBACK_TOKEN: z.string().min(1),
    BASE_DOMAIN: z.string().min(1),
  },

  /**
   * Specify your client-side environment variables schema here. This way you can ensure the app
   * isn't built with invalid env vars. To expose them to the client, prefix them with
   * `NEXT_PUBLIC_`.
   */
  shared: {
    NEXT_PUBLIC_VERCEL_PROJECT_PRODUCTION_URL: z.url(),
    NODE_ENV: z
      .enum(["development", "test", "production"])
      .prefault("development"),
    NEXT_PUBLIC_APP_NAME: z.string().min(1),
  },
  client: {
    NEXT_PUBLIC_GOOGLE_MAPS_API_KEY: z.string(),
    NEXT_PUBLIC_GOOGLE_MAPS_MAP_ID_LIGHT: z.string(),
    NEXT_PUBLIC_GOOGLE_MAPS_MAP_ID_DARK: z.string(),
    NEXT_PUBLIC_SIMCALL_ORG_ID: z.string(),
  },
  /**
   * You can't destruct `process.env` as a regular object in the Next.js edge runtimes (e.g.
   * middlewares) or client-side so we need to destruct manually.
   */
  runtimeEnv: {
    DATABASE_URL: process.env.DATABASE_URL,
    NODE_ENV: process.env.NODE_ENV,
    NEXT_PUBLIC_VERCEL_PROJECT_PRODUCTION_URL: getBaseUrl(),
    BETTER_AUTH_SECRET: process.env.BETTER_AUTH_SECRET,
    NEXT_PUBLIC_SIMCALL_ORG_ID: process.env.NEXT_PUBLIC_SIMCALL_ORG_ID,
    NETRIFI_API_KEY: process.env.NETRIFI_API_KEY,
    NETRIFI_API_KEY_2: process.env.NETRIFI_API_KEY_2,
    NEXT_PUBLIC_GOOGLE_MAPS_API_KEY:
      process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY,
    NEXT_PUBLIC_GOOGLE_MAPS_MAP_ID_LIGHT:
      process.env.NEXT_PUBLIC_GOOGLE_MAPS_MAP_ID_LIGHT,
    NEXT_PUBLIC_GOOGLE_MAPS_MAP_ID_DARK:
      process.env.NEXT_PUBLIC_GOOGLE_MAPS_MAP_ID_DARK,
    DRIZZLE_LOG: process.env.DRIZZLE_LOG,
    SUPER_ORG_CUSTOM_EMAILS: process.env.SUPER_ORG_CUSTOM_EMAILS,
    SUPER_ORG_EMAIL_DOMAIN: process.env.SUPER_ORG_EMAIL_DOMAIN,
    WEBHOOK_SECRET: process.env.WEBHOOK_SECRET,
    INTERNAL_API_KEY: process.env.INTERNAL_API_KEY,
    SENTRY_AUTH_TOKEN: process.env.SENTRY_AUTH_TOKEN,
    TRIGGER_SECRET_KEY: process.env.TRIGGER_SECRET_KEY,
    TELEGRAM_BOT_TOKEN: process.env.TELEGRAM_BOT_TOKEN,
    TELEGRAM_INFO_CHAT_ID: process.env.TELEGRAM_INFO_CHAT_ID,
    TELEGRAM_WARNING_CHAT_ID: process.env.TELEGRAM_WARNING_CHAT_ID,
    TELEGRAM_ERROR_CHAT_ID: process.env.TELEGRAM_ERROR_CHAT_ID,
    TELEGRAM_USER_FEEDBACK_CHAT_ID: process.env.TELEGRAM_USER_FEEDBACK_CHAT_ID,
    AWS_SIMCALL_OLD_DB_URL: process.env.AWS_SIMCALL_OLD_DB_URL,
    WHITELISTED_SERVICES_PROXY_IP: process.env.WHITELISTED_SERVICES_PROXY_IP,
    WHITELISTED_SERVICES_PROXY_PORT:
      process.env.WHITELISTED_SERVICES_PROXY_PORT,
    WHITELISTED_SERVICES_PROXY_USERNAME:
      process.env.WHITELISTED_SERVICES_PROXY_USERNAME,
    WHITELISTED_SERVICES_PROXY_PASSWORD:
      process.env.WHITELISTED_SERVICES_PROXY_PASSWORD,
    EDGE_CONFIG: process.env.EDGE_CONFIG,
    RESEND_API_KEY: process.env.RESEND_API_KEY,
    SLACK_BOT_TOKEN: process.env.SLACK_BOT_TOKEN,
    SLACK_APP_TOKEN: process.env.SLACK_APP_TOKEN,
    AWS_REGION: process.env.AWS_REGION,
    AWS_ACCESS_KEY_ID: process.env.AWS_ACCESS_KEY_ID,
    AWS_SECRET_ACCESS_KEY: process.env.AWS_SECRET_ACCESS_KEY,
    AUTH_IMAGES_CDN_URL: process.env.AUTH_IMAGES_CDN_URL,
    GC_SERVICE_ACCOUNT_EMAIL: process.env.GC_SERVICE_ACCOUNT_EMAIL,
    GC_SERVICE_ACCOUNT_PRIVATE_KEY: process.env.GC_SERVICE_ACCOUNT_PRIVATE_KEY,
    MISTRAL_API_KEY: process.env.MISTRAL_API_KEY,
    DISABLE_REACT_SCAN: process.env.DISABLE_REACT_SCAN,
    SMS_GATEWAY_CALLBACK_TOKEN: process.env.SMS_GATEWAY_CALLBACK_TOKEN,
    BASE_DOMAIN: process.env.BASE_DOMAIN,
    NEXT_PUBLIC_APP_NAME: process.env.NEXT_PUBLIC_APP_NAME,
  },
  /**
   * Run `build` or `dev` with `SKIP_ENV_VALIDATION` to skip env validation. This is especially
   * useful for Docker builds.
   */
  skipValidation: Boolean(process.env.SKIP_ENV_VALIDATION),
  /**
   * Makes it so that empty strings are treated as undefined. `SOME_VAR: z.string()` and
   * `SOME_VAR=''` will throw an error.
   */
  emptyStringAsUndefined: true,
});
